import { t } from "i18next";

import DarkModeIcon from "@mui/icons-material/DarkMode";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeIcon from "@mui/icons-material/LightMode";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { Box, ButtonBase, Typography } from "@mui/material";

import { isDarkMode as isDarkScheme } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

import useThemeMode from "@/src/hooks/useThemeMode";
import { setDigitalDataThemeMode } from "@/src/utils/digitalData/themeMode";

import {
  getIconSx,
  getKnobSx,
  getLabelSx,
  getRootSx,
  getTrackSx,
  type ToggleIcon,
  type ToggleSurface
} from "./themeToggle.styles";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";

interface ThemeToggleProps {
  userType: "consumer" | "dealer" | "admin";
  showLabel?: boolean;
  surface?: ToggleSurface;
}

/** Sx for the icon variant shown while the reader is in `shownInDark` mode. */
const iconSx = (
  icon: ToggleIcon,
  shownInDark: boolean,
  surface: ToggleSurface
) =>
  schemeSx((theme) => {
    const dark = isDarkScheme(theme);

    return {
      ...getIconSx(theme, icon, dark, surface),
      display: dark === shownInDark ? "inline-block" : "none"
    };
  });

const ThemeToggle = (props: ThemeToggleProps) => {
  const { userType, showLabel = false, surface = "header" } = props;
  const sendDataToGTM = useGTMDispatch();
  const { isDarkMode, toggleThemeMode } = useThemeMode();

  const label = isDarkMode ? t("general.darkMode") : t("general.lightMode");

  const handleToggle = () => {
    const themeMode = isDarkMode ? "light" : "dark";
    sendDataToGTM({
      event: "theme_mode_changed",
      theme_mode: themeMode,
      user_type: userType
    });
    setDigitalDataThemeMode(themeMode);
    toggleThemeMode();
  };

  return (
    <ButtonBase
      disableRipple
      role="switch"
      aria-checked={isDarkMode}
      aria-label={t("general.darkMode")}
      onClick={handleToggle}
      sx={getRootSx}
    >
      {/*
        The knob and icons read the scheme from CSS rather than from
        `useThemeMode`, which only knows the reader's scheme once React has
        hydrated. The prerendered header would otherwise paint the light
        position first and then slide over. Each icon slot renders both of its
        variants and the scheme selector shows one.
      */}
      <Box sx={schemeSx((theme) => getTrackSx(theme, surface))}>
        <Box sx={schemeSx((theme) => getKnobSx(theme, isDarkScheme(theme)))} />
        <LightModeIcon sx={iconSx("light", false, surface)} />
        <LightModeOutlinedIcon sx={iconSx("light", true, surface)} />
        <DarkModeOutlinedIcon sx={iconSx("dark", false, surface)} />
        <DarkModeIcon sx={iconSx("dark", true, surface)} />
      </Box>
      {showLabel && (
        <Typography variant="body2" sx={getLabelSx}>
          {label}
        </Typography>
      )}
    </ButtonBase>
  );
};

export default ThemeToggle;
